const getSlaByPriority = (priority) => {
  switch (priority) {
    case 'Critical': return 15;
    case 'High': return 30;
    case 'Medium': return 60;
    default: return 240;
  }
};

const specificSimulations = {
  // --- TIER 1 TICKETS (1001-1050) ---
  "TKT-1001": {
    q: "RESOURCE ACCESS: Cannot access shared Z: drive. Shared network resources are unreachable.",
    o: [
      { t: "Verify VPN status, run 'net use /delete' to clear stale mounts, and remap using the full FQDN path.", c: true, f: "Correct. Clearing stale mounts is the first step in resolving persistent mapping errors.", p: 20 },
      { t: "Grant the user 'Domain Admin' rights temporarily to see if the drive maps.", c: false, f: "SECURITY VIOLATION: Never escalate privileges to troubleshoot a mapping error.", p: -100 },
      { t: "Reboot the departmental file server to refresh all connections.", c: false, f: "CRITICAL IMPACT: Do not disrupt hundreds of users for a single-user connectivity issue.", p: -80 }
    ]
  },
  "TKT-1011": {
    q: "VIP AVAILABILITY ALERT: CEO cannot join board Zoom meeting.",
    o: [
      { t: "Provide an immediate dial-in bridge or mobile workaround to ensure meeting continuity first.", c: true, f: "Excellent. For VIPs, business continuity must happen before deep technical troubleshooting.", p: 50 },
      { t: "Begin a remote screen-share session to update drivers and check system logs.", c: false, f: "CRITICAL FAILURE: Never perform invasive troubleshooting during a live executive meeting.", p: -100 },
      { t: "Escalate to the Infrastructure Lead and wait for them to contact the Executive.", c: false, f: "Too slow. The meeting is happening now. You must provide an immediate alternative path.", p: -40 }
    ]
  },
  "TKT-1020": {
    q: "SECURITY PROTOCOL: Offboarding request for terminated employee.",
    o: [
      { t: "Immediately disable the AD account, revoke all active M365 sessions, and block sign-in.", c: true, f: "Correct. Immediate containment is required to prevent data exfiltration.", p: 50 },
      { t: "Wait until the end of the day so they can finish sending their final emails.", c: false, f: "SECURITY VIOLATION: You must never leave a terminated employee's access active.", p: -100 },
      { t: "Change their password but leave their current active sessions logged in.", c: false, f: "INCOMPLETE: Active sessions will remain connected if not explicitly revoked.", p: -60 }
    ]
  },
  "TKT-1034": {
    q: "DATA LOSS PREVENTION: Email containing confidential attachment sent to wrong recipient.",
    o: [
      { t: "Trigger an immediate PowerShell 'Search-Mailbox -DeleteContent' to purge the message from the recipient's inbox.", c: true, f: "Perfect. Message recall is unreliable; a server-side purge is the only way to ensure containment.", p: 50 },
      { t: "Use the 'Recall this Message' feature in Outlook and wait for a success notification.", c: false, f: "RELIABILITY FAILURE: Outlook recall only works if the recipient hasn't opened it.", p: -50 },
      { t: "Contact the recipient via phone and ask them to delete the email and empty their trash.", c: false, f: "SECURITY RISK: You cannot rely on a third-party's cooperation for containment.", p: -60 }
    ]
  },
  "TKT-1050": {
    q: "VIP SECURITY PROTOCOL: Need urgent password reset while traveling internationally.",
    o: [
      { t: "Strictly verify identity via HR records and alternate out-of-band contact before resetting.", c: true, f: "Correct. VIPs are prime targets for whaling/social engineering. Never bypass verification.", p: 50 },
      { t: "Trust the caller's voice and provide a temporary password immediately to avoid downtime.", c: false, f: "CRITICAL FAILURE: Voice cloning exists. You just facilitated an account takeover.", p: -100 },
      { t: "Ask their Administrative Assistant to vouch for them on a recorded line.", c: false, f: "INCOMPLETE: Vouching is not a substitute for official identity verification records.", p: -40 }
    ]
  },

  // --- TIER 2 TICKETS (2001-2050) ---
  "TKT-2001": {
    q: "AD REPLICATION: Domain Controllers DC01 and DC02 are out of sync. Users in Branch B cannot login.",
    o: [
      { t: "Run 'repadmin /replsummary' to identify the failure, then check DNS SRV records and RPC service health.", c: true, f: "Correct. DNS and RPC are the primary requirements for AD replication.", p: 40 },
      { t: "Perform an authoritative restore of the entire Active Directory database.", c: false, f: "CRITICAL RISK: Authoritative restores are high-risk operations. Diagnose the transport layer first.", p: -100 },
      { t: "Delete the DC02 computer object and rejoin it to the domain.", c: false, f: "Incorrect. This causes metadata cleanup issues and should only be done as a last resort.", p: -60 }
    ]
  },
  "TKT-2002": {
    q: "DATABASE PERFORMANCE: SQL Server reporting 'Deadlock' errors on the primary ERP database.",
    o: [
      { t: "Enable Trace Flag 1222 to capture deadlock info in the error log and analyze the execution plan.", c: true, f: "Excellent. Capturing the specific deadlock graph is essential for optimization.", p: 40 },
      { t: "Restart the SQL Server service to clear all active connections.", c: false, f: "CRITICAL IMPACT: Restarting production DBs causes massive downtime and doesn't fix the query logic.", p: -100 },
      { t: "Increase CPU and RAM on the SQL Virtual Machine.", c: false, f: "Inefficient. Deadlocks are logic issues, not resource starvation issues.", p: -40 }
    ]
  },
  "TKT-2003": {
    q: "CLOUD SECURITY: AWS S3 bucket 'finance-backups' policy is reporting public access exposure.",
    o: [
      { t: "Enable 'Block Public Access' at the account level and audit the bucket ACLs for 'AuthenticatedUsers' access.", c: true, f: "Correct. Account-level blocking is the fastest way to remediate global exposure.", p: 50 },
      { t: "Delete the bucket and recreate it with a private name.", c: false, f: "Incorrect. Bucket names must be unique globally; deleting it risks losing the name and all data.", p: -80 },
      { t: "Change the bucket prefix to 'private-finance-backups'.", c: false, f: "SECURITY FAIL: Renaming folders does not change the underlying access policy.", p: -60 }
    ]
  },
  "TKT-2004": {
    q: "IDENTITY SYNC: Azure AD Connect reports 'Staging Mode' error. No local password changes are syncing to M365.",
    o: [
      { t: "Verify if another server has taken the Active role and check the 'ADSync' service account permissions.", c: true, f: "Correct. Azure AD Connect only allows one active exporter; staging mode prevents conflicts.", p: 40 },
      { t: "Reinstall the Azure AD Connect client on the Domain Controller.", c: false, f: "Overkill. Configuration issues should be resolved before reinstallation.", p: -40 },
      { t: "Tell users to change their passwords directly in the M365 portal.", c: false, f: "Non-compliant. This breaks the single-source-of-truth model for identity.", p: -50 }
    ]
  },
  "TKT-2005": {
    q: "VIRTUALIZATION: VMware ESXi host unresponsive. 15 production VMs are in an unknown state.",
    o: [
      { t: "Check physical IPMI/iDRAC for hardware errors and verify if HA (High Availability) triggered a failover.", c: true, f: "Correct. Hardware-level access is required when the hypervisor OS is unresponsive.", p: 40 },
      { t: "Attempt to vMotion the VMs to a different host.", c: false, f: "Impossible. vMotion requires the source host to be responsive and communicating with vCenter.", p: -60 },
      { t: "Power cycle the SAN (Storage Area Network) switch.", c: false, f: "CRITICAL IMPACT: You just crashed every host connected to the storage network.", p: -100 }
    ]
  },
  "TKT-2006": {
    q: "EMAIL INFRASTRUCTURE: Exchange DAG (Database Availability Group) has lost quorum. All DBs are offline.",
    o: [
      { t: "Verify the Cluster Witness Server availability and force quorum if necessary using 'Start-ClusterNode -FixQuorum'.", c: true, f: "Correct. Losing quorum brings down the entire mail cluster; fixing the witness is priority one.", p: 50 },
      { t: "Mount the databases individually on each server.", c: false, f: "Impossible. Databases cannot mount without a healthy cluster quorum.", p: -40 },
      { t: "Restore the Exchange servers from a snapshot.", c: false, f: "CRITICAL RISK: Restoring cluster nodes from snapshots causes massive database corruption.", p: -100 }
    ]
  },
  "TKT-2007": {
    q: "NETWORK INFRASTRUCTURE: New VLAN 200 configured on Core Switch, but users cannot pull DHCP.",
    o: [
      { t: "Verify the 'ip helper-address' is configured on the VLAN interface and check trunk port tags.", c: true, f: "Correct. IP helpers are required to route DHCP broadcasts across VLAN boundaries.", p: 40 },
      { t: "Reboot the DHCP server.", c: false, f: "Inefficient. If other VLANs work, the server is fine; it's a routing/helper issue.", p: -30 },
      { t: "Disable the firewall on the DHCP server.", c: false, f: "SECURITY RISK: Do not disable security for a routing configuration error.", p: -60 }
    ]
  },
  "TKT-2008": {
    q: "CONTAINERIZATION: Kubernetes Pod 'api-service' is in 'CrashLoopBackOff' state.",
    o: [
      { t: "Run 'kubectl logs' and 'kubectl describe pod' to check for environment variable mismatches or probe failures.", c: true, f: "Excellent. Logs and descriptions are the primary troubleshooting tools for K8s.", p: 40 },
      { t: "Delete the entire Namespace and recreate it.", c: false, f: "Overkill. This destroys all other healthy services in the namespace.", p: -80 },
      { t: "Increase the CPU limit in the deployment manifest.", c: false, f: "Guesswork. Diagnose the crash reason before scaling resources.", p: -30 }
    ]
  },
  "TKT-2009": {
    q: "STORAGE: SAN volume 'LUN_01' is at 99% capacity. Production apps are failing to write logs.",
    o: [
      { t: "Identify and purge stale snapshots, then expand the LUN size in the storage array and OS.", c: true, f: "Correct. Snapshots often consume hidden space on enterprise storage.", p: 40 },
      { t: "Delete the pagefile.sys on the production servers.", c: false, f: "CRITICAL RISK: Deleting the pagefile causes immediate system instability and crashes.", p: -100 },
      { t: "Format the volume to clear space.", c: false, f: "CRITICAL DATA LOSS: Formatting production volumes is a fireable offense.", p: -100 }
    ]
  },
  "TKT-2010": {
    q: "BGP ROUTING: ISP reports BGP session is 'Down'. Regional office lost all connectivity.",
    o: [
      { t: "Check the local router interface status and verify the MD5 authentication string matches the ISP's config.", c: true, f: "Correct. Authentication mismatches are a common cause of BGP peer failures.", p: 50 },
      { t: "Restart the OSPF process on the core switch.", c: false, f: "Incorrect. OSPF is internal; BGP is external. They are separate protocols.", p: -40 },
      { t: "Set a static route to 0.0.0.0/0 via the ISP gateway.", c: false, f: "INCOMPLETE: Static routes don't handle failover or prefix advertisement like BGP.", p: -30 }
    ]
  }
};

const getSimulation = (id, cat, title) => {
  const sim = specificSimulations[id];
  if (sim) {
    return {
      steps: [
        {
          id: 'step1',
          question: sim.q,
          options: sim.o.map((opt, idx) => ({
            id: idx + 1,
            text: opt.t,
            nextStep: opt.c ? 'success' : 'fail_error',
            correct: opt.c,
            feedback: opt.f,
            points: opt.p
          }))
        }
      ]
    };
  }

  return {
    steps: [
      {
        id: 'step1',
        question: `ADVANCED ANALYSIS: Incident ${id} regarding "${title}". How do you proceed?`,
        options: [
          { id: 1, text: "Analyze system logs (Event Viewer/Syslog) and cross-reference with the Knowledge Base SOP.", nextStep: 'success', correct: true, feedback: "Correct. Tier 2 relies on log-driven diagnostics.", points: 20 },
          { id: 2, text: "Escalate immediately to the Vendor (Microsoft/AWS/Cisco) without internal triage.", nextStep: 'fail_escalation', correct: false, feedback: "Inefficient. Tier 2 must perform internal triage before vendor escalation.", points: -30 },
          { id: 3, text: "Apply a global configuration change to all servers to see if it fixes the issue.", nextStep: 'fail_impact', correct: false, feedback: "CRITICAL RISK: Never change global config without testing in a sandbox.", points: -100 }
        ]
      }
    ]
  };
};

const rawData = [
  // TIER 1 (1001-1050)
  ["TKT-1001","Sarah Jenkins","Marketing","Cannot access shared Z: drive","Access","High"],
  ["TKT-1002","David Chen","Engineering","Laptop continuously crashing with BSOD after login","Hardware","Critical"],
  ["TKT-1003","Emily Rodriguez","HR","Suspicious email asking to verify payroll login","Security","High"],
  ["TKT-1004","Marcus Johnson","Sales","No internet connection at desk after docking laptop","Network","Critical"],
  ["TKT-1005","Lisa Lopez","Sales","Teams microphone not detected during client calls","Audio","Medium"],
  ["TKT-1006","Karen King","Legal","Password reset link expired and account locked","Password","Medium"],
  ["TKT-1007","Patricia Perez","Marketing","Printer on 2nd floor shows offline","Printer","Medium"],
  ["TKT-1008","Linda Roberts","Finance","MFA prompt not reaching mobile device","MFA","High"],
  ["TKT-1009","Michael Tran","Operations","Outlook stuck on “Trying to connect”","Email","High"],
  ["TKT-1010","Amanda Johnson","HR","New hire needs laptop, email, and Teams access by 9 AM","Onboarding","High"],
  ["TKT-1011","Robert Hill","Executive","CEO cannot join board Zoom meeting","VIP / Conferencing","Critical"],
  ["TKT-1012","Jasmine Patel","Product","Adobe Acrobat missing after recent device refresh","Software","Low"],
  ["TKT-1013","Noah Garcia","Warehouse","Barcode scanner not pairing with workstation","Peripheral","Medium"],
  ["TKT-1014","Olivia Moore","Finance","Excel file on SharePoint opens read-only for everyone","SharePoint","High"],
  ["TKT-1015","Ethan Brooks","Engineering","VPN disconnects every 5 minutes from home office","VPN","High"],
  ["TKT-1016","Sophia Rivera","Customer Support","Headset audio only coming through one ear","Hardware","Low"],
  ["TKT-1017","Daniel Scott","Sales","Cannot send email to external client domain","Email","Medium"],
  ["TKT-1018","Chloe Evans","Product","Need access to Jira project “Phoenix”","Access","Low"],
  ["TKT-1019","Victor Alvarez","Legal","Word crashes when opening one specific contract template","Software","Medium"],
  ["TKT-1020","Grace Kim","HR","Offboarding request for terminated employee","Offboarding","Critical"],
  ["TKT-1021","Tyler Reed","Operations","Shared conference room calendar missing from Outlook","M365","Medium"],
  ["TKT-1022","Monica Flores","Finance","Payroll folder permissions denied after department move","Permissions","High"],
  ["TKT-1023","Brandon Lee","Engineering","Cannot RDP into lab workstation","Remote Access","Medium"],
  ["TKT-1024","Hannah Ward","Marketing","Company website images not loading on office network","Network / DNS","High"],
  ["TKT-1025","Adrian Torres","Customer Support","Browser homepage hijacked after installing free PDF tool","Security","High"],
  ["TKT-1026","Megan Cook","Product","Slack notifications stopped on mobile only","Mobile / App","Low"],
  ["TKT-1027","Jose Ramirez","Facilities","Label printer printing blank stickers","Printer","Medium"],
  ["TKT-1028","Rachel Bell","Finance","QuickBooks multi-user mode not working","Application","High"],
  ["TKT-1029","Kevin Nguyen","Engineering","External monitor flickering through USB-C dock","Hardware","Medium"],
  ["TKT-1030","Brooke Adams","HR","Need distribution list updated for new recruiting team","Email Admin","Low"],
  ["TKT-1031","Steven Price","Sales","CRM page loads extremely slowly for all reps","SaaS / Performance","High"],
  ["TKT-1032","Natalie Cruz","Executive","Assistant accidentally deleted meeting series","Calendar","Medium"],
  ["TKT-1033","Cameron White","Operations","Cannot print to PDF from browser after update","Software","Low"],
  ["TKT-1034","Isabella Martinez","Legal","Email sent to wrong recipient (Confidential)","Security / DLP","Critical"],
  ["TKT-1035","Aaron Foster","Warehouse","Wi-Fi drops near loading dock every afternoon","Wireless","Medium"],
  ["TKT-1036","Vanessa Diaz","Marketing","Need Canva and Dropbox installed","Software Request","Low"],
  ["TKT-1037","Julian Morris","Finance","Laptop BitLocker recovery key prompt","Endpoint / Encryption","High"],
  ["TKT-1038","Leah Simmons","Support","Customer call recordings missing in Teams","Telephony","High"],
  ["TKT-1039","Christopher Hall","Engineering","Git access denied after MFA reset","Access / Dev Tools","Medium"],
  ["TKT-1040","Zoe Bennett","Product","OneDrive sync conflict duplicated project folder","OneDrive","Medium"],
  ["TKT-1041","Henry Phillips","Sales","iPhone company email stopped syncing","Mobile Email","Medium"],
  ["TKT-1042","Maya Russell","HR","Interview room webcam not detected","AV / Conferencing","High"],
  ["TKT-1043","Derek Cooper","Finance","Need access to former employee mailbox","Email / Compliance","High"],
  ["TKT-1044","Elena Gutierrez","Marketing","Shared mailbox “events@” missing","M365","Low"],
  ["TKT-1045","Gavin Ross","Engineering","Local admin rights requested for chipset utility","Permissions","Medium"],
  ["TKT-1046","Priya Shah","Legal","Secure file transfer portal rejecting uploads","Application","Medium"],
  ["TKT-1047","Oscar Diaz","Support","Call queue available but no calls routing","Telephony","Critical"],
  ["TKT-1048","Mia Turner","Finance","Bank site access blocked by security policy","Security / Web Access","High"],
  ["TKT-1049","Lucas Green","Operations","Desktop will not wake from sleep","Hardware","Low"],
  ["TKT-1050","Abigail Parker","Executive","Urgent password reset while traveling","Password / VIP","High"],

  // TIER 2 (2001-2050) - HUMANIZED REQUESTERS
  ["TKT-2001","Alex Rivera","Infrastructure","Active Directory Replication Failure","Server Admin","Critical"],
  ["TKT-2002","Samantha Wu","Database","SQL Server Deadlock Detected","Database","High"],
  ["TKT-2003","Jordan Smith","Security","S3 Bucket Public Exposure Detected","Cloud","Critical"],
  ["TKT-2004","Michael O'Connor","Identity","Azure AD Connect Staging Mode Error","Identity","High"],
  ["TKT-2005","Elena Petrova","Virtualization","ESXi Host Unresponsive - 15 VMs Down","Virtualization","Critical"],
  ["TKT-2006","Jameson Blake","M365","Exchange DAG Quorum Lost","M365","Critical"],
  ["TKT-2007","Fatima Zahra","Infrastructure","VLAN 200 DHCP Helper Failure","Network","High"],
  ["TKT-2008","Xavier Thorne","DevOps","Kubernetes Pod CrashLoopBackOff","Cloud","Medium"],
  ["TKT-2009","Marcus Vane","Infrastructure","SAN Volume LUN_01 at 99% Capacity","Storage","High"],
  ["TKT-2010","Leona Sterling","Network","BGP Peering Session Down","Infrastructure","Critical"],
  ["TKT-2011","Viktor Krum","Security","Brute Force Attack Detected on VPN Gateway","Security","Critical"],
  ["TKT-2012","Silas Thorne","Infrastructure","Group Policy Object (GPO) Corruption","Server Admin","High"],
  ["TKT-2013","Nina Simone","Database","PostgreSQL Connection Pool Exhaustion","Database","High"],
  ["TKT-2014","Oliver Queen","Cloud","AWS DirectConnect Latency > 200ms","Cloud","High"],
  ["TKT-2015","Sarah Connor","Identity","M365 Global Admin Account Compromised","Security","Critical"],
  ["TKT-2016","Miles Morales","MDM","Intune Policy Conflict - 500 Devices Non-Compliant","MDM","High"],
  ["TKT-2017","Barry Allen","Telephony","SIP Trunk Registration Failure","Telephony","High"],
  ["TKT-2018","Diana Prince","Virtualization","Cluster Shared Volume (CSV) Offline","Virtualization","Critical"],
  ["TKT-2019","Clark Kent","Infrastructure","Internal DNS Zone Scavenging Error","Server Admin","Medium"],
  ["TKT-2020","Bruce Wayne","Security","Ransomware Canary File Triggered","Security","Critical"],
  ["TKT-2021","Natasha Romanoff","M365","Exchange Online Protection (EOP) False Positive Spike","M365","High"],
  ["TKT-2022","Tony Stark","Database","SQL Server Transaction Log Full","Database","High"],
  ["TKT-2023","Wanda Maximoff","Cloud","Azure ExpressRoute Circuit Down","Cloud","Critical"],
  ["TKT-2024","Steve Rogers","Infrastructure","STP Bridge Root Conflict","Network","High"],
  ["TKT-2025","Carol Danvers","Security","WAF Blocking Legitimate API Traffic","Security","High"],
  ["TKT-2026","T'Challa Udaku","Server Admin","Kernel Panic on Web-Prod-04","Server Admin","High"],
  ["TKT-2027","Peter Parker","M365","Autodiscover Service Unresponsive","M365","High"],
  ["TKT-2028","Stephen Strange","Infrastructure","RAID 6 Double Disk Failure Alert","Storage","Critical"],
  ["TKT-2029","Arthur Curry","Identity","Okta to AD Sync Agent Offline","Identity","High"],
  ["TKT-2030","Victor Stone","Cloud","Terraform State Lock Conflict","Cloud","Medium"],
  ["TKT-2031","Hal Jordan","Network","OSPF Neighbor Adjacency Failure","Infrastructure","High"],
  ["TKT-2032","Logan Howlett","Security","Unauthorized Sudo Attempt on Root Server","Security","High"],
  ["TKT-2033","Jean Grey","Virtualization","VDA Registration Failure - XenApp","Virtualization","High"],
  ["TKT-2034","Scott Summers","M365","SharePoint Site Collection Quota Reached","M365","Medium"],
  ["TKT-2035","Charles Xavier","Database","MongoDB Shard Imbalance","Database","High"],
  ["TKT-2036","Erik Lehnsherr","Cloud","AWS IAM Role Permission Boundary Violation","Security","High"],
  ["TKT-2037","Ororo Munroe","MDM","Apple Push Notification Service (APNS) Expired","MDM","Critical"],
  ["TKT-2038","Hank McCoy","Telephony","QoS Tagging Mismatch on WAN Link","Telephony","Medium"],
  ["TKT-2039","Reed Richards","Server Admin","Print Nightmare Vulnerability Patching","Security","High"],
  ["TKT-2040","Susan Storm","Storage","LTO-8 Tape Library Mechanical Failure","Storage","Medium"],
  ["TKT-2041","Johnny Storm","Network","MTU Mismatch on Tunnel Interface","Infrastructure","High"],
  ["TKT-2042","Ben Grimm","Security","Suspicious PowerShell Execution via WMI","Security","Critical"],
  ["TKT-2043","Matt Murdock","Cloud","Azure Load Balancer Health Probe Failure","Cloud","High"],
  ["TKT-2044","Jennifer Walters","Server Admin","NFS Mount Points Stale across Cluster","Server Admin","High"],
  ["TKT-2045","Luke Cage","Infrastructure","SSL Certificate Chain Incomplete","Infrastructure","Medium"],
  ["TKT-2046","Danny Rand","Identity","SAML Metadata Expiration (Salesforce)","Identity","High"],
  ["TKT-2047","Frank Castle","Virtualization","vCenter Server Appliance (VCSA) DB Full","Virtualization","High"],
  ["TKT-2048","Jessica Jones","Network","Dot1x Authentication Failure - 10th Floor","Network","High"],
  ["TKT-2049","Wade Wilson","Security","DLP Alert: Large File Upload to Mega.nz","Security","High"],
  ["TKT-2050","Nick Fury","Infrastructure","Global ERP System Unresponsive","Infrastructure","Critical"]
];

export const initialTickets = rawData.map(([id, user, dept, title, cat, prio], index) => {
  const tier = id.startsWith('TKT-2') ? 2 : 1;
  return {
    id,
    tier,
    user,
    department: dept,
    avatar: `https://i.pravatar.cc/150?u=${encodeURIComponent(user)}`,
    title,
    category: cat.split(' / ')[0],
    description: `Incident Report ${id}: ${title}. Reported by ${user} (${dept}). Critical system impact suspected.`,
    priority: prio,
    slaMinutes: getSlaByPriority(prio),
    status: 'open',
    openedAt: Date.now() - (index * 5 * 60000),
    systemInfo: {
      os: tier === 2 ? 'Enterprise Linux / Server Core' : 'Windows 11',
      ip: `10.50.${tier}.${10 + index}`,
      device: `${dept.substring(0,3).toUpperCase()}-${tier === 2 ? 'SRV' : 'WS'}-${id.split('-')[1]}`
    },
    simulation: getSimulation(id, cat.split(' / ')[0], title)
  };
});